import ssm from './vendor/aws-ssm-session.js';

export interface SsmTerminalConnectOpts {
  streamUrl: string;
  tokenValue: string;
  cols: number;
  rows: number;
  onOutput: (text: string) => void;
  onClose: (reason: string) => void;
}

const NUL_CHAR = String.fromCharCode(0);

/**
 * decode() returns messageType as a fixed-width field, left-padded with NUL (code point 0)
 * characters, string right-justified. String.prototype.trim() does not strip NUL, so a plain
 * .trim() is a no-op on these values - strip the NUL padding explicitly before comparing.
 */
export function normalizeMessageType(raw: string): string {
  return raw.split(NUL_CHAR).join('').trim();
}

/**
 * Thin typed wrapper over the vendored AWS Session Manager protocol module: token handshake,
 * init (terminal size), input frames with the module's sequence tracking, acks for
 * output_stream_data, and channel_closed handling. One instance per session/tab.
 * No keepalive on purpose - SSM's idle-session timeout is a security control; the page offers
 * Reconnect instead.
 */
export class SsmTerminal {
  private socket: WebSocket | null = null;
  private opts: SsmTerminalConnectOpts | null = null;
  private textDecoder = new TextDecoder();
  private closed = false;

  connect(opts: SsmTerminalConnectOpts): void {
    this.opts = opts;
    this.closed = false;
    const socket = new WebSocket(opts.streamUrl);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    socket.onopen = () => {
      ssm.init(socket, { token: opts.tokenValue, termOptions: { cols: opts.cols, rows: opts.rows } });
    };
    socket.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      const message = ssm.decode(event.data);
      const messageType = normalizeMessageType(message.messageType);
      if (messageType === 'output_stream_data') {
        ssm.sendACK(socket, message);
        // PayloadType 1 = terminal output; other payload types (handshake metadata) are ignored.
        if (message.payloadType === 1) opts.onOutput(this.textDecoder.decode(message.payload));
      } else if (messageType === 'channel_closed') {
        this.signalClose('Session ended');
        socket.close();
      }
    };
    socket.onclose = () => this.signalClose('Connection closed');
    socket.onerror = () => this.signalClose('Connection error');
  }

  sendText(text: string): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    // The vendored protocol module (sha256/putString, vendor/sha256.js + vendor/aws-ssm-session.js)
    // is Latin-1-only: it treats each JS string char code as one output byte. A char code above
    // 0xFF either throws inside the vendored sha256 (emoji/CJK - dropping the keystroke) or gets
    // silently truncated to its low byte by putString's charCodeAt(0) (mangling accented input
    // like 'é'). Per the do-not-edit-vendored-code ruling, encode here instead: this turns `text`
    // into a "UTF-8 binary string" where each character code IS one UTF-8 byte (always <= 0xFF),
    // so every char-code check downstream in the vendored module succeeds, and the bytes it frames
    // onto the wire are, byte-for-byte, valid UTF-8 for the remote shell to decode.
    const utf8 = unescape(encodeURIComponent(text));
    ssm.sendText(this.socket, utf8);
  }

  resize(cols: number, rows: number): void {
    if (this.socket?.readyState === WebSocket.OPEN) ssm.sendInitMessage(this.socket, { cols, rows });
  }

  close(): void {
    this.closed = true;
    this.socket?.close();
    this.socket = null;
  }

  // onclose/onerror can fire after an intentional close() (or after the channel_closed handler
  // already reported); guard so callers only ever see one onClose per session.
  private signalClose(reason: string): void {
    if (this.closed) return;
    this.closed = true;
    this.opts?.onClose(reason);
  }
}
